import { Injectable } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import {
  combineLatest,
  filter,
  first,
  firstValueFrom,
  map,
  Observable,
  startWith,
} from 'rxjs';
import {
  BoelsPostalCodeHandlerMap,
  Country,
  PostalCode,
  PostalCodeTranslations,
} from '@boels/models';

@Injectable()
export class FormService {
  setFormValidators(form: FormGroup, isTradeCustomer: boolean): void {
    const companyNameControl = form.controls?.['tradeCustomer'].get(
      'companyName',
    ) as AbstractControl;

    if (isTradeCustomer) {
      companyNameControl.setValidators(Validators.required);
    } else {
      companyNameControl.clearValidators();
    }
    companyNameControl.updateValueAndValidity();
  }

  setCountryInitials(
    form: FormGroup,
    countries$: Observable<{ countries: Country[] }>,
  ): void {
    countries$
      .pipe(
        filter((countries) => Boolean(countries.countries.length)),
        first(),
      )
      .subscribe((countries) => {
        if (!form.controls?.['country'].value.isocode) {
          form.controls?.['country'].patchValue({
            isocode: countries.countries[0]?.isocode,
          });
        }
      });
  }

  isRequired(control: AbstractControl): boolean {
    return control?.validator?.({} as AbstractControl)?.['required'];
  }

  setHouseNumberValidation(form: FormGroup, culture$: Observable<string>) {
    const houseNumberControl = form.get('line2');
    const exceptionalCulture = 'en-gb';
    if (houseNumberControl)
      culture$
        .pipe(
          map((culture: string) => {
            const validators =
              culture === exceptionalCulture ? [] : [Validators.required];
            houseNumberControl.setValidators(validators);
            houseNumberControl.setValue(houseNumberControl.value, {
              emitEvent: false,
            });
          }),
          first(),
        )
        .subscribe();
  }

  postalCodeHandler(activeCulture: string): BoelsPostalCodeHandlerMap {
    return {
      NL: {
        isValidFormat: (postalCode: string) => {
          const nlRegex = /^\d{4}\s?[A-Za-z]{2}$/;
          return nlRegex.test(postalCode);
        },
        placeholderText: '1234 AB',
        positionOfSpace: 4,
        validationMessage:
          activeCulture.split('-')[0] === 'nl'
            ? PostalCodeTranslations.NL
            : PostalCodeTranslations.FallbackNL,
      },

      PL: {
        isValidFormat: (postalCode: string) => {
          const plRegex = /^\d{2}[\s-]?\d{3}$/;
          return plRegex.test(postalCode);
        },
        placeholderText: '12-345',
        positionOfSpace: 2,
        validationMessage:
          activeCulture.split('-')[0] === 'pl'
            ? PostalCodeTranslations.PL
            : PostalCodeTranslations.FallbackPL,
      },

      SK: {
        isValidFormat: (postalCode: string) => {
          const skRegex = /^\d{3}\s?\d{2}$/;
          return skRegex.test(postalCode);
        },
        placeholderText: '123 45',
        positionOfSpace: 3,
        validationMessage:
          activeCulture.split('-')[0] === 'sk'
            ? PostalCodeTranslations.SK
            : PostalCodeTranslations.FallbackSk,
      },
    };
  }

  async isLocaleCountrySelectedAs(
    languageCode: string,
    culture$: Observable<string>,
  ): Promise<boolean> {
    const activeCulture = await firstValueFrom(
      culture$.pipe(map((culture: string) => culture.split('-')[0])),
    );

    return activeCulture === languageCode;
  }

  setPostalCodeValidation(
    form: FormGroup,
    culture$: Observable<string>,
  ): Observable<PostalCode | null> {
    const countryControl = this.getFormItem(form, 'country');
    const postalCodeControl = this.getFormItem(form, 'postalCode');

    return combineLatest([
      postalCodeControl.valueChanges.pipe(startWith(postalCodeControl.value)),
      countryControl.valueChanges.pipe(startWith(countryControl.value)),
      culture$,
    ]).pipe(
      map(([postalCode, country, culture]) => {
        const postalCodeHandlerMap = this.postalCodeHandler(
          culture.toLowerCase(),
        );
        const postalCodeHandler = postalCodeHandlerMap[country.isocode];
        if (!postalCodeHandler) {
          if (postalCodeControl.value) postalCodeControl.setErrors(null);
          return null;
        }

        const postalCodePlaceholder = postalCodeHandler.placeholderText;
        const postalCodeValidationMessage = postalCodeHandler.validationMessage;
        const postalCodePositionOfSpace = postalCodeHandler.positionOfSpace;
        const controlMessages = {
          postalCodePlaceholder,
          postalCodeValidationMessage,
          postalCodePositionOfSpace,
        };

        if (postalCodeHandler.isValidFormat(postalCode)) {
          postalCodeControl.setErrors(null);
        } else if (postalCodeControl.value) {
          postalCodeControl.setErrors({ postalCodePattern: true });
        }
        return controlMessages;
      }),
    );
  }

  formatPostalCode(value: string, positionOfSpace: number): string {
    // Formatting for PL
    if (value.includes('-')) {
      value = value.replace(/(\d{2})-(\d{3})/, '$1 $2');
    }

    if (!value || !positionOfSpace) return value;

    if (
      value.length >= positionOfSpace &&
      value.charAt(positionOfSpace) !== ' '
    ) {
      return (
        value.slice(0, positionOfSpace) + ' ' + value.slice(positionOfSpace)
      );
    }
    return value;
  }

  getFormItem<T extends FormControl | FormGroup>(
    form: FormGroup,
    key: string,
    nestedKey?: string,
  ): T {
    if (!nestedKey) {
      if (!form.controls[key]) {
        throw new Error(
          `FormControl with key '${key}' does not exist in the FormGroup`,
        );
      }
      return form.controls[key] as T;
    }
    if (!form.controls[key]?.get(nestedKey)) {
      throw new Error(
        `FormControl with key '${key}' and nested key '${nestedKey}' does not exist in the FormGroup`,
      );
    }
    return form.controls[key].get(nestedKey) as T;
  }
}
